import React, { useState, useEffect, useRef, useContext, createContext, useCallback } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useParams, useLocation, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Home, History, ChevronRight, Check, Loader2, Info, Plus, Trash2, ArrowLeft, FileDown, FileSpreadsheet, LogOut, Shield, User as UserIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// 啟用 cookie 跨域傳送 (Emergent Google Auth)
axios.defaults.withCredentials = true;

// ---------------- Auth Context ----------------
const AuthContext = createContext({ user: null, loading: true, refresh: () => {}, logout: () => {} });
const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // 若 URL 帶 session_id,讓 AuthCallback 處理,先別跑 /me
    if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch (e) { /* ignore */ }
    setUser(null);
    window.location.href = '/login';
  };

  return (
    <AuthContext.Provider value={{ user, loading, refresh, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// ---------------- Login Page ----------------
const formatApiErrorDetail = (detail) => {
  if (detail == null) return '發生錯誤，請重試';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

const LoginPage = () => {
  const { user, loading, refresh } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState('login'); // 'login' | 'register'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <Loader2 className="h-8 w-8 animate-spin text-[#0F52BA]" />
      </div>
    );
  }
  if (user) return <Navigate to="/" replace />;

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      toast.error('請輸入 Email 與密碼');
      return;
    }
    if (mode === 'register' && password.length < 6) {
      toast.error('密碼至少需 6 個字元');
      return;
    }
    setSubmitting(true);
    try {
      const endpoint = mode === 'register' ? '/auth/register' : '/auth/login';
      const payload = mode === 'register' 
        ? { email, password, name } 
        : { email, password };
      const res = await axios.post(`${API}${endpoint}`, payload);
      toast.success(mode === 'register' ? '註冊成功!' : `歡迎,${res.data.user?.name || ''}`);
      await refresh();
      navigate('/', { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || '操作失敗,請重試';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-[#0F52BA] tracking-tight mb-2">SWOT 分析系統</h1>
          <p className="text-slate-600">登入以使用專業的策略分析工具</p>
        </div>
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl text-center">
              {mode === 'login' ? '歡迎回來' : '建立新帳號'}
            </CardTitle>
            <CardDescription className="text-center">
              {mode === 'login' ? '請選擇登入方式' : '註冊以開始使用'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Email/Password 表單 */}
            <form onSubmit={handleEmailSubmit} className="space-y-3">
              {mode === 'register' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">名稱 <span className="text-slate-400 text-xs">(選填)</span></label>
                  <Input
                    data-testid="register-name-input"
                    type="text"
                    placeholder="您的顯示名稱"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                <Input
                  data-testid="auth-email-input"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  密碼 {mode === 'register' && <span className="text-slate-400 text-xs">(至少 6 字元)</span>}
                </label>
                <Input
                  data-testid="auth-password-input"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <Button
                data-testid={mode === 'register' ? 'register-submit-button' : 'login-submit-button'}
                type="submit"
                disabled={submitting}
                className="w-full h-11 bg-[#0F52BA] hover:bg-[#0c4296]"
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  mode === 'register' ? '註冊並登入' : '登入'
                )}
              </Button>
            </form>
            
            {/* 切換 mode */}
            <div className="text-center text-sm text-slate-600">
              {mode === 'login' ? (
                <>
                  尚未有帳號？
                  <button
                    data-testid="switch-to-register"
                    type="button"
                    onClick={() => setMode('register')}
                    className="text-[#0F52BA] font-medium hover:underline ml-1"
                  >
                    立即註冊
                  </button>
                </>
              ) : (
                <>
                  已有帳號？
                  <button
                    data-testid="switch-to-login"
                    type="button"
                    onClick={() => setMode('login')}
                    className="text-[#0F52BA] font-medium hover:underline ml-1"
                  >
                    返回登入
                  </button>
                </>
              )}
            </div>
            
            {/* 分隔線 */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-white px-3 text-slate-500">或</span>
              </div>
            </div>
            
            {/* Google 登入 */}
            <Button
              data-testid="google-login-button"
              type="button"
              onClick={handleGoogleLogin}
              variant="outline"
              className="w-full h-11 bg-white hover:bg-slate-50 text-slate-800 border-2 border-slate-200"
            >
              <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              使用 Google 登入
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ---------------- Auth Callback ----------------
const AuthCallback = () => {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || '';
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) {
      navigate('/login', { replace: true });
      return;
    }
    const sessionId = m[1];

    (async () => {
      try {
        const res = await axios.post(`${API}/auth/session`, null, {
          headers: { 'X-Session-ID': sessionId }
        });
        // 清掉 hash
        window.history.replaceState(null, '', window.location.pathname);
        await refresh();
        toast.success(`歡迎，${res.data.user?.name || ''}`);
        navigate('/', { replace: true });
      } catch (e) {
        toast.error('登入失敗，請重試');
        navigate('/login', { replace: true });
      }
    })();
  }, [navigate, refresh]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#F8FAFC]">
      <Loader2 className="h-8 w-8 animate-spin text-[#0F52BA] mb-4" />
      <p className="text-slate-600">登入處理中...</p>
    </div>
  );
};

// ---------------- Protected Route ----------------
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  // 若 URL 帶 session_id, 允許通過 (由內層的 AuthCallback / RootRouter 處理)
  if (location.hash?.includes('session_id=')) {
    return children;
  }
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <Loader2 className="h-8 w-8 animate-spin text-[#0F52BA]" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
};


// Export Buttons Component
const ExportButtons = ({ analysisId }) => {
  const [pdfLoading, setPdfLoading] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);

  const handleExport = async (format) => {
    const setLoading = format === 'pdf' ? setPdfLoading : setExcelLoading;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/analysis/${analysisId}/export/${format}`, {
        responseType: 'blob',
      });
      // Try to extract filename from Content-Disposition
      let filename = `SWOT_${analysisId.slice(0, 8)}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      const disposition = response.headers['content-disposition'];
      if (disposition) {
        const m = disposition.match(/filename\*=UTF-8''([^;]+)/);
        if (m && m[1]) {
          try { filename = decodeURIComponent(m[1]); } catch (e) { /* keep fallback */ }
        }
      }
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`已匯出 ${format === 'pdf' ? 'PDF' : 'Excel'} 檔案`);
    } catch (error) {
      toast.error('匯出失敗，請重試');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex gap-2">
      <Button
        data-testid="export-pdf-button"
        onClick={() => handleExport('pdf')}
        disabled={pdfLoading}
        variant="outline"
        className="border-[#0F52BA] text-[#0F52BA] hover:bg-blue-50"
      >
        {pdfLoading ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <FileDown className="mr-2 h-4 w-4" />
        )}
        匯出 PDF
      </Button>
      <Button
        data-testid="export-excel-button"
        onClick={() => handleExport('excel')}
        disabled={excelLoading}
        variant="outline"
        className="border-[#16A34A] text-[#16A34A] hover:bg-green-50"
      >
        {excelLoading ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <FileSpreadsheet className="mr-2 h-4 w-4" />
        )}
        匯出 Excel
      </Button>
    </div>
  );
};

// Layout Component
const Layout = ({ children, currentView, setCurrentView }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isTodosPage = location.pathname === '/todos';
  
  return (
    <div className="min-h-screen flex">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-slate-200 fixed h-full flex flex-col">
        <div className="p-6">
          <h1 className="text-2xl font-bold text-[#0F52BA] tracking-tight">SWOT 分析系統</h1>
          <p className="text-sm text-slate-500 mt-1">策略分析工具</p>
        </div>
        
        <nav className="mt-6 flex-1">
          <button
            data-testid="nav-new-analysis"
            onClick={() => { if (isTodosPage) navigate('/'); setCurrentView('new'); }}
            className={`w-full flex items-center gap-3 px-6 py-3 text-left transition-colors ${
              !isTodosPage && currentView === 'new' ? 'bg-blue-50 text-[#0F52BA] border-r-2 border-[#0F52BA]' : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            <Home size={20} />
            <span className="font-medium">新增分析</span>
          </button>
          
          <button
            data-testid="nav-history"
            onClick={() => { if (isTodosPage) navigate('/'); setCurrentView('history'); }}
            className={`w-full flex items-center gap-3 px-6 py-3 text-left transition-colors ${
              !isTodosPage && currentView === 'history' ? 'bg-blue-50 text-[#0F52BA] border-r-2 border-[#0F52BA]' : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            <History size={20} />
            <span className="font-medium">{user?.is_admin ? '所有分析' : '我的分析'}</span>
          </button>
          
          <button
            data-testid="nav-todos"
            onClick={() => navigate('/todos')}
            className={`w-full flex items-center gap-3 px-6 py-3 text-left transition-colors ${
              isTodosPage ? 'bg-blue-50 text-[#0F52BA] border-r-2 border-[#0F52BA]' : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            <Check size={20} />
            <span className="font-medium">待辦事項清單</span>
          </button>
        </nav>
        
        {/* User Info Section */}
        {user && (
          <div className="border-t border-slate-200 p-4">
            <div className="flex items-center gap-3 mb-3" data-testid="user-info">
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="w-9 h-9 rounded-full" />
              ) : (
                <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center">
                  <UserIcon size={18} className="text-slate-500" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate" title={user.name}>{user.name || user.email}</p>
                <div className="flex items-center gap-1 mt-0.5">
                  {user.is_admin && (
                    <Badge className="bg-amber-100 text-amber-700 border-amber-300 text-xs px-1.5 py-0">
                      <Shield size={10} className="mr-0.5" />Admin
                    </Badge>
                  )}
                  <p className="text-xs text-slate-500 truncate" title={user.email}>{user.email}</p>
                </div>
              </div>
            </div>
            <Button
              data-testid="logout-button"
              onClick={logout}
              variant="outline"
              size="sm"
              className="w-full text-slate-600 hover:text-slate-800"
            >
              <LogOut size={14} className="mr-2" />
              登出
            </Button>
          </div>
        )}
      </aside>
      
      {/* Main Content */}
      <main className="flex-1 ml-64 p-8">
        {children}
      </main>
    </div>
  );
};

// Stepper Component
const Stepper = ({ currentStep, onStepClick, canNavigate = false }) => {
  const steps = [
    { number: 1, title: '基本資料', key: 'step1' },
    { number: 2, title: '外部環境', key: 'step2' },
    { number: 3, title: '內部條件', key: 'step3' },
    { number: 4, title: '策略分析', key: 'completed' },
  ];
  
  const currentStepNumber = steps.findIndex(s => s.key === currentStep) + 1;
  
  return (
    <div className="mb-8">
      <div className="flex items-center justify-between max-w-3xl mx-auto">
        {steps.map((step, index) => {
          const canClick = canNavigate && onStepClick && step.number < currentStepNumber;
          return (
            <React.Fragment key={step.number}>
              <div className="flex flex-col items-center">
                <button
                  data-testid={`step-${step.number}`}
                  onClick={() => canClick && onStepClick(step.number - 1)}
                  disabled={!canClick}
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors ${
                    currentStepNumber >= step.number
                      ? 'bg-[#0F52BA] text-white'
                      : 'bg-slate-200 text-slate-500'
                  } ${canClick ? 'cursor-pointer hover:ring-2 hover:ring-blue-300' : 'cursor-default'}`}
                >
                  {currentStepNumber > step.number ? <Check size={20} /> : step.number}
                </button>
                <span className="text-sm mt-2 font-medium text-slate-600">{step.title}</span>
              </div>
              {index < steps.length - 1 && (
                <div className={`flex-1 h-0.5 mx-4 ${
                  currentStepNumber > step.number ? 'bg-[#0F52BA]' : 'bg-slate-200'
                }`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

// Step 1: Basic Info
const Step1BasicInfo = ({ analysisId, setAnalysisId, onNext, existingData, setAnalysisData }) => {
  const [industryType, setIndustryType] = useState(existingData?.industry_type || '');
  const [companySize, setCompanySize] = useState(existingData?.company_size || '');
  const [coreBusiness, setCoreBusiness] = useState(existingData?.core_business || '');
  const [targetMarketRegion, setTargetMarketRegion] = useState(existingData?.target_market_region || '');
  const [targetCustomers, setTargetCustomers] = useState(existingData?.target_customers || '');
  const [marketCharacteristics, setMarketCharacteristics] = useState(existingData?.market_characteristics || '');
  const [analysisType, setAnalysisType] = useState(existingData?.analysis_type || '經營管理');
  const [loading, setLoading] = useState(false);
  
  // 當 existingData 變化（例如從 Stepper 回跳）時同步欄位
  useEffect(() => {
    if (existingData) {
      setIndustryType(existingData.industry_type || '');
      setCompanySize(existingData.company_size || '');
      setCoreBusiness(existingData.core_business || '');
      setTargetMarketRegion(existingData.target_market_region || '');
      setTargetCustomers(existingData.target_customers || '');
      setMarketCharacteristics(existingData.market_characteristics || '');
      setAnalysisType(existingData.analysis_type || '經營管理');
    }
  }, [existingData?.id]);
  
  const companySizeOptions = [
    { value: '微型企業(1-9人)', label: '微型企業（1-9 人）' },
    { value: '小型企業(10-49人)', label: '小型企業（10-49 人）' },
    { value: '中型企業(50-249人)', label: '中型企業（50-249 人）' },
    { value: '大型企業(250人以上)', label: '大型企業（250 人以上）' },
  ];
  
  const analysisTypes = [
    { value: '經營管理', label: '經營管理', desc: '營運策略、市場競爭、財務管理' },
    { value: '環境管理', label: '環境管理', desc: '永續發展、ESG、綠色供應鏈' },
    { value: '能源管理', label: '能源管理', desc: '能源效率、再生能源、節能減碳' },
    { value: '安全衛生管理', label: '安全衛生管理', desc: '職業安全、健康衛生、工安管理' },
  ];
  
  const handleSubmit = async () => {
    if (!industryType.trim() || !companySize.trim() || !coreBusiness.trim() || 
        !targetMarketRegion.trim() || !marketCharacteristics.trim()) {
      toast.error('請填寫所有必填欄位');
      return;
    }
    
    setLoading(true);
    const payload = {
      industry_type: industryType,
      company_size: companySize,
      core_business: coreBusiness,
      target_market_region: targetMarketRegion,
      target_customers: targetCustomers,
      market_characteristics: marketCharacteristics,
      analysis_type: analysisType || '經營管理'
    };
    
    try {
      if (analysisId) {
        // 更新現有分析
        const res = await axios.put(`${API}/analysis/${analysisId}/basic-info`, payload);
        if (res.data.reset) {
          toast.success('分析項目已變更，後續分析將重新生成');
        } else {
          toast.success('基本資料已更新');
        }
        // 同步 wizard 中的 analysisData
        if (setAnalysisData) {
          const refreshed = await axios.get(`${API}/analysis/${analysisId}`);
          setAnalysisData(refreshed.data);
        }
      } else {
        // 建立新分析
        const response = await axios.post(`${API}/analysis/create`, payload);
        setAnalysisId(response.data.id);
        if (setAnalysisData) setAnalysisData(response.data);
        toast.success('基本資料已儲存');
      }
      onNext();
    } catch (error) {
      toast.error('儲存失敗，請重試');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="max-w-3xl mx-auto animate-in fade-in slide-in-from-right-4 duration-300">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">企業基本資料</CardTitle>
          <CardDescription>請填寫企業相關資訊以進行 SWOT 分析</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              產業類型 <span className="text-red-500">*</span>
            </label>
            <Input
              data-testid="industry-type-input"
              placeholder="例如：製造業、服務業、科技業、零售業..."
              value={industryType}
              onChange={(e) => setIndustryType(e.target.value)}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              企業規模 <span className="text-red-500">*</span>
            </label>
            <Select value={companySize} onValueChange={setCompanySize}>
              <SelectTrigger data-testid="company-size-select" className="w-full bg-white">
                <SelectValue placeholder="請選擇企業規模" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {companySizeOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value} data-testid={`company-size-option-${opt.value}`}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              核心業務 <span className="text-red-500">*</span>
            </label>
            <Textarea
              data-testid="core-business-input"
              placeholder="請描述企業主要產品或服務..."
              value={coreBusiness}
              onChange={(e) => setCoreBusiness(e.target.value)}
              className="min-h-[80px]"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              目標市場地區 <span className="text-red-500">*</span>
            </label>
            <Input
              data-testid="target-market-region-input"
              placeholder="例如：台灣、東南亞、北美、全球..."
              value={targetMarketRegion}
              onChange={(e) => setTargetMarketRegion(e.target.value)}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              目標客群 <span className="text-slate-400 text-xs">(選填)</span>
            </label>
            <Input
              data-testid="target-customers-input"
              placeholder="例如：年輕女性消費者、B2B 中小企業、銀髮族..."
              value={targetCustomers}
              onChange={(e) => setTargetCustomers(e.target.value)}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              市場特性 <span className="text-red-500">*</span>
            </label>
            <Textarea
              data-testid="market-characteristics-input"
              placeholder="請描述目標市場的特性，例如：消費習慣、競爭程度、成長趨勢..."
              value={marketCharacteristics}
              onChange={(e) => setMarketCharacteristics(e.target.value)}
              className="min-h-[80px]"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-3">
              分析項目 <span className="text-slate-400 text-xs">(未選擇時預設為「經營管理」)</span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              {analysisTypes.map((type) => (
                <div
                  key={type.value}
                  data-testid={`analysis-type-${type.value}`}
                  onClick={() => setAnalysisType(type.value)}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    analysisType === type.value
                      ? 'border-[#0F52BA] bg-blue-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      analysisType === type.value ? 'border-[#0F52BA]' : 'border-slate-300'
                    }`}>
                      {analysisType === type.value && (
                        <div className="w-2 h-2 rounded-full bg-[#0F52BA]"></div>
                      )}
                    </div>
                    <span className="font-medium text-slate-800">{type.label}</span>
                  </div>
                  <p className="text-xs text-slate-500 ml-6">{type.desc}</p>
                </div>
              ))}
            </div>
          </div>
          
          <Button
            data-testid="step1-next-button"
            onClick={handleSubmit}
            disabled={loading}
            className="w-full bg-[#0F52BA] hover:bg-[#0c4296] h-11"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                處理中...
              </>
            ) : (
              <>
                確認並繼續
                <ChevronRight className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

// Step 2: PESTLE Analysis
const Step2PESTLE = ({ analysisId, analysisData, setAnalysisData, onNext }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [generated, setGenerated] = useState(false);
  const [selectedOpportunities, setSelectedOpportunities] = useState([]);
  const [selectedThreats, setSelectedThreats] = useState([]);
  const [customOpportunities, setCustomOpportunities] = useState([]);
  const [customThreats, setCustomThreats] = useState([]);
  const [customOppInput, setCustomOppInput] = useState('');
  const [customThreatInput, setCustomThreatInput] = useState('');
  
  useEffect(() => {
    if (analysisData?.pestle_analysis?.length > 0) {
      setGenerated(true);
      // 預設全選所有AI建議的機會與威脅
      const allOpps = analysisData.opportunities.map(o => o.issue);
      const allThreats = analysisData.threats.map(t => t.issue);
      if (analysisData.selected_opportunities?.length > 0) {
        setSelectedOpportunities(analysisData.selected_opportunities);
      } else {
        setSelectedOpportunities(allOpps);
      }
      if (analysisData.selected_threats?.length > 0) {
        setSelectedThreats(analysisData.selected_threats);
      } else {
        setSelectedThreats(allThreats);
      }
      setCustomOpportunities(analysisData.custom_opportunities || []);
      setCustomThreats(analysisData.custom_threats || []);
    }
  }, [analysisData]);
  
  const handleGenerate = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/analysis/${analysisId}/generate-pestle`);
      const response = await axios.get(`${API}/analysis/${analysisId}`);
      setAnalysisData(response.data);
      setGenerated(true);
      // 預設全選
      setSelectedOpportunities(response.data.opportunities.map(o => o.issue));
      setSelectedThreats(response.data.threats.map(t => t.issue));
      toast.success('PESTLE分析已生成');
    } catch (error) {
      toast.error('生成失敗，請重試');
    } finally {
      setLoading(false);
    }
  };
  
  const toggleOpportunity = (issue) => {
    setSelectedOpportunities(prev =>
      prev.includes(issue) ? prev.filter(o => o !== issue) : [...prev, issue]
    );
  };
  
  const toggleThreat = (issue) => {
    setSelectedThreats(prev =>
      prev.includes(issue) ? prev.filter(t => t !== issue) : [...prev, issue]
    );
  };
  
  const handleAddCustomOpp = () => {
    if (customOppInput.trim()) {
      setCustomOpportunities([...customOpportunities, customOppInput.trim()]);
      setCustomOppInput('');
      toast.success('已新增自訂機會');
    }
  };
  
  const handleAddCustomThreat = () => {
    if (customThreatInput.trim()) {
      setCustomThreats([...customThreats, customThreatInput.trim()]);
      setCustomThreatInput('');
      toast.success('已新增自訂威脅');
    }
  };
  
  const handleRemoveCustomOpp = (index) => {
    setCustomOpportunities(customOpportunities.filter((_, i) => i !== index));
  };
  
  const handleRemoveCustomThreat = (index) => {
    setCustomThreats(customThreats.filter((_, i) => i !== index));
  };
  
  const handleNext = async () => {
    const totalOpps = selectedOpportunities.length + customOpportunities.length;
    const totalThreats = selectedThreats.length + customThreats.length;
    
    if (totalOpps === 0 || totalThreats === 0) {
      toast.error('請至少選擇或新增一項機會和一項威脅');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/analysis/${analysisId}/update-ot`, {
        selected_opportunities: selectedOpportunities,
        selected_threats: selectedThreats,
        custom_opportunities: customOpportunities,
        custom_threats: customThreats
      });
      toast.success('已儲存選擇');
      onNext();
    } catch (error) {
      toast.error('儲存失敗，請重試');
    } finally {
      setSubmitting(false);
    }
  };
  
  const getCategoryColor = (category) => {
    const colors = {
      'Political': 'bg-purple-100 text-purple-700 border-purple-200',
      'Economic': 'bg-green-100 text-green-700 border-green-200',
      'Social': 'bg-blue-100 text-blue-700 border-blue-200',
      'Technological': 'bg-indigo-100 text-indigo-700 border-indigo-200',
      'Legal': 'bg-amber-100 text-amber-700 border-amber-200',
      'Environmental': 'bg-emerald-100 text-emerald-700 border-emerald-200',
    };
    return colors[category] || 'bg-gray-100 text-gray-700 border-gray-200';
  };
  
  const opportunities = analysisData?.opportunities || [];
  const threats = analysisData?.threats || [];
  
  return (
    <div className="max-w-5xl mx-auto animate-in fade-in slide-in-from-right-4 duration-300">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">PESTLE 外部環境分析</CardTitle>
          <CardDescription>勾選您想納入後續分析的機會與威脅，或新增自訂項目</CardDescription>
        </CardHeader>
        <CardContent>
          {!generated ? (
            <div className="text-center py-12">
              <p className="text-slate-600 mb-6">點擊下方按鈕，AI 將為您生成 PESTLE 分析</p>
              <Button
                data-testid="generate-pestle-button"
                onClick={handleGenerate}
                disabled={loading}
                className="bg-[#0F52BA] hover:bg-[#0c4296]"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    生成中...
                  </>
                ) : (
                  '生成 PESTLE 分析'
                )}
              </Button>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Opportunities */}
              <div>
                <h3 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#2563EB]"></div>
                  機會 Opportunities
                  <span className="text-sm font-normal text-slate-500 ml-2">
                    已選 {selectedOpportunities.length + customOpportunities.length} 項
                  </span>
                </h3>
                <div className="grid gap-3 mb-4">
                  {opportunities.map((item, index) => {
                    const isSelected = selectedOpportunities.includes(item.issue);
                    return (
                      <div
                        key={index}
                        data-testid={`opportunity-item-${index}`}
                        className={`border rounded-lg transition-all ${
                          isSelected ? 'border-[#2563EB] bg-blue-50' : 'border-slate-200'
                        }`}
                      >
                        <div className="p-4">
                          <div className="flex items-start justify-between gap-4">
                            <div
                              className="flex items-start gap-3 cursor-pointer flex-1"
                              onClick={() => toggleOpportunity(item.issue)}
                            >
                              <Checkbox
                                checked={isSelected}
                                className="mt-1"
                                data-testid={`opportunity-checkbox-${index}`}
                              />
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <Badge className={getCategoryColor(item.category)}>
                                    {item.category}
                                  </Badge>
                                </div>
                                <p className="text-slate-700 font-medium">{item.issue}</p>
                              </div>
                            </div>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    data-testid={`opportunity-reason-${index}`}
                                    className="text-slate-400 hover:text-slate-600 mt-1"
                                  >
                                    <Info size={18} />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                  <p className="text-sm">{item.reason}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* Custom Opportunities */}
                {customOpportunities.length > 0 && (
                  <div className="grid gap-2 mb-4">
                    {customOpportunities.map((opp, index) => (
                      <div
                        key={`custom-opp-${index}`}
                        className="flex items-center justify-between p-3 border border-[#2563EB] bg-blue-50 rounded-lg"
                      >
                        <div className="flex items-center gap-2">
                          <Badge className="bg-blue-100 text-blue-700">自訂</Badge>
                          <span className="text-slate-700">{opp}</span>
                        </div>
                        <button
                          onClick={() => handleRemoveCustomOpp(index)}
                          className="text-red-500 hover:text-red-700"
                          data-testid={`remove-custom-opp-${index}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="flex gap-2">
                  <Input
                    data-testid="custom-opportunity-input"
                    placeholder="新增自訂機會..."
                    value={customOppInput}
                    onChange={(e) => setCustomOppInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddCustomOpp()}
                  />
                  <Button
                    data-testid="add-custom-opportunity-button"
                    onClick={handleAddCustomOpp}
                    variant="outline"
                  >
                    <Plus size={18} />
                  </Button>
                </div>
              </div>
              
              {/* Threats */}
              <div>
                <h3 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#D97706]"></div>
                  威脅 Threats
                  <span className="text-sm font-normal text-slate-500 ml-2">
                    已選 {selectedThreats.length + customThreats.length} 項
                  </span>
                </h3>
                <div className="grid gap-3 mb-4">
                  {threats.map((item, index) => {
                    const isSelected = selectedThreats.includes(item.issue);
                    return (
                      <div
                        key={index}
                        data-testid={`threat-item-${index}`}
                        className={`border rounded-lg transition-all ${
                          isSelected ? 'border-[#D97706] bg-amber-50' : 'border-slate-200'
                        }`}
                      >
                        <div className="p-4">
                          <div className="flex items-start justify-between gap-4">
                            <div
                              className="flex items-start gap-3 cursor-pointer flex-1"
                              onClick={() => toggleThreat(item.issue)}
                            >
                              <Checkbox
                                checked={isSelected}
                                className="mt-1"
                                data-testid={`threat-checkbox-${index}`}
                              />
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <Badge className={getCategoryColor(item.category)}>
                                    {item.category}
                                  </Badge>
                                </div>
                                <p className="text-slate-700 font-medium">{item.issue}</p>
                              </div>
                            </div>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    data-testid={`threat-reason-${index}`}
                                    className="text-slate-400 hover:text-slate-600 mt-1"
                                  >
                                    <Info size={18} />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                  <p className="text-sm">{item.reason}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* Custom Threats */}
                {customThreats.length > 0 && (
                  <div className="grid gap-2 mb-4">
                    {customThreats.map((threat, index) => (
                      <div
                        key={`custom-threat-${index}`}
                        className="flex items-center justify-between p-3 border border-[#D97706] bg-amber-50 rounded-lg"
                      >
                        <div className="flex items-center gap-2">
                          <Badge className="bg-amber-100 text-amber-700">自訂</Badge>
                          <span className="text-slate-700">{threat}</span>
                        </div>
                        <button
                          onClick={() => handleRemoveCustomThreat(index)}
                          className="text-red-500 hover:text-red-700"
                          data-testid={`remove-custom-threat-${index}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="flex gap-2">
                  <Input
                    data-testid="custom-threat-input"
                    placeholder="新增自訂威脅..."
                    value={customThreatInput}
                    onChange={(e) => setCustomThreatInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddCustomThreat()}
                  />
                  <Button
                    data-testid="add-custom-threat-button"
                    onClick={handleAddCustomThreat}
                    variant="outline"
                  >
                    <Plus size={18} />
                  </Button>
                </div>
              </div>
              
              <Button
                data-testid="step2-next-button"
                onClick={handleNext}
                disabled={submitting}
                className="w-full bg-[#0F52BA] hover:bg-[#0c4296] h-11"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    處理中...
                  </>
                ) : (
                  <>
                    確認並繼續
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Step 3: Strengths & Weaknesses
const Step3SW = ({ analysisId, analysisData, setAnalysisData, onNext }) => {
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(false);
  const [selectedStrengths, setSelectedStrengths] = useState([]);
  const [selectedWeaknesses, setSelectedWeaknesses] = useState([]);
  const [customStrength, setCustomStrength] = useState('');
  const [customWeakness, setCustomWeakness] = useState('');
  
  useEffect(() => {
    if (analysisData?.strength_options?.length > 0) {
      setGenerated(true);
      setSelectedStrengths(analysisData.selected_strengths || []);
      setSelectedWeaknesses(analysisData.selected_weaknesses || []);
    }
  }, [analysisData]);
  
  const handleGenerate = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/analysis/${analysisId}/generate-sw-options`);
      const response = await axios.get(`${API}/analysis/${analysisId}`);
      setAnalysisData(response.data);
      setGenerated(true);
      toast.success('優勢劣勢選項已生成');
    } catch (error) {
      toast.error('生成失敗，請重試');
    } finally {
      setLoading(false);
    }
  };
  
  const handleAddCustomStrength = () => {
    if (customStrength.trim()) {
      setSelectedStrengths([...selectedStrengths, customStrength.trim()]);
      setCustomStrength('');
      toast.success('已新增自訂優勢');
    }
  };
  
  const handleAddCustomWeakness = () => {
    if (customWeakness.trim()) {
      setSelectedWeaknesses([...selectedWeaknesses, customWeakness.trim()]);
      setCustomWeakness('');
      toast.success('已新增自訂劣勢');
    }
  };
  
  const handleNext = async () => {
    if (selectedStrengths.length === 0 || selectedWeaknesses.length === 0) {
      toast.error('請至少選擇一項優勢和一項劣勢');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/analysis/${analysisId}/update-sw`, {
        selected_strengths: selectedStrengths,
        selected_weaknesses: selectedWeaknesses
      });
      toast.success('優勢劣勢已儲存');
      onNext();
    } catch (error) {
      toast.error('儲存失敗，請重試');
    } finally {
      setLoading(false);
    }
  };
  
  const toggleStrength = (text) => {
    setSelectedStrengths(prev =>
      prev.includes(text) ? prev.filter(s => s !== text) : [...prev, text]
    );
  };
  
  const toggleWeakness = (text) => {
    setSelectedWeaknesses(prev =>
      prev.includes(text) ? prev.filter(w => w !== text) : [...prev, text]
    );
  };
  
  return (
    <div className="max-w-5xl mx-auto animate-in fade-in slide-in-from-right-4 duration-300">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">內部條件分析</CardTitle>
          <CardDescription>選擇或自訂企業的優勢與劣勢</CardDescription>
        </CardHeader>
        <CardContent>
          {!generated ? (
            <div className="text-center py-12">
              <p className="text-slate-600 mb-6">點擊下方按鈕，AI 將為您生成優勢與劣勢選項</p>
              <Button
                data-testid="generate-sw-button"
                onClick={handleGenerate}
                disabled={loading}
                className="bg-[#0F52BA] hover:bg-[#0c4296]"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    生成中...
                  </>
                ) : (
                  '生成優勢劣勢選項'
                )}
              </Button>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Strengths */}
              <div>
                <h3 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#16A34A]"></div>
                  優勢 Strengths
                </h3>
                <div className="grid gap-3 mb-4">
                  {analysisData.strength_options.map((option, index) => (
                    <div
                      key={index}
                      data-testid={`strength-option-${index}`}
                      onClick={() => toggleStrength(option.text)}
                      className={`p-4 border rounded-lg cursor-pointer transition-all ${
                        selectedStrengths.includes(option.text)
                          ? 'border-[#16A34A] bg-green-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Checkbox checked={selectedStrengths.includes(option.text)} />
                        <span className="text-slate-700">{option.text}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    data-testid="custom-strength-input"
                    placeholder="輸入自訂優勢..."
                    value={customStrength}
                    onChange={(e) => setCustomStrength(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddCustomStrength()}
                  />
                  <Button
                    data-testid="add-custom-strength-button"
                    onClick={handleAddCustomStrength}
                    variant="outline"
                  >
                    <Plus size={18} />
                  </Button>
                </div>
              </div>
              
              {/* Weaknesses */}
              <div>
                <h3 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#DC2626]"></div>
                  劣勢 Weaknesses
                </h3>
                <div className="grid gap-3 mb-4">
                  {analysisData.weakness_options.map((option, index) => (
                    <div
                      key={index}
                      data-testid={`weakness-option-${index}`}
                      onClick={() => toggleWeakness(option.text)}
                      className={`p-4 border rounded-lg cursor-pointer transition-all ${
                        selectedWeaknesses.includes(option.text)
                          ? 'border-[#DC2626] bg-red-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Checkbox checked={selectedWeaknesses.includes(option.text)} />
                        <span className="text-slate-700">{option.text}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    data-testid="custom-weakness-input"
                    placeholder="輸入自訂劣勢..."
                    value={customWeakness}
                    onChange={(e) => setCustomWeakness(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddCustomWeakness()}
                  />
                  <Button
                    data-testid="add-custom-weakness-button"
                    onClick={handleAddCustomWeakness}
                    variant="outline"
                  >
                    <Plus size={18} />
                  </Button>
                </div>
              </div>
              
              <Button
                data-testid="step3-next-button"
                onClick={handleNext}
                disabled={loading}
                className="w-full bg-[#0F52BA] hover:bg-[#0c4296] h-11"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    處理中...
                  </>
                ) : (
                  <>
                    確認並繼續
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Strategy Card with inclusion decision
const StrategyCard = ({ strategy, index, analysisId, onUpdate }) => {
  const [inclusion, setInclusion] = useState(strategy.inclusion || 'pending');
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const inclusionConfig = {
    pending: { label: '尚未決定', dot: 'bg-slate-400', border: 'border-l-slate-300', bg: 'bg-slate-50', textColor: 'text-slate-600' },
    included: { label: '已納入待辦', dot: 'bg-blue-500', border: 'border-l-blue-500', bg: 'bg-blue-50', textColor: 'text-blue-700' },
    deferred: { label: '暫緩實施', dot: 'bg-amber-500', border: 'border-l-amber-500', bg: 'bg-amber-50', textColor: 'text-amber-700' },
  };

  const handleInclusion = async (newVal) => {
    const prev = inclusion;
    setInclusion(newVal);
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/analysis/${analysisId}/strategies/${index}/inclusion`,
        { inclusion: newVal }
      );
      if (onUpdate) {
        onUpdate(index, { inclusion: newVal, inclusion_updated_at: res.data.inclusion_updated_at });
      }
      toast.success(newVal === 'included' ? '已納入待辦事項' : newVal === 'deferred' ? '已標記為暫緩' : '已重設');
    } catch (e) {
      setInclusion(prev);
      toast.error('更新失敗，請重試');
    } finally {
      setSaving(false);
    }
  };

  const cfg = inclusionConfig[inclusion] || inclusionConfig.pending;

  return (
    <Card data-testid={`strategy-${index}`} className={`border-l-4 ${cfg.border}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <p className="text-slate-800 font-medium text-lg flex-1">{strategy.strategy}</p>
          <div className="flex items-center gap-2 shrink-0">
            <span className={`w-2 h-2 rounded-full ${cfg.dot}`}></span>
            <span className={`text-xs font-medium ${cfg.textColor}`}>{cfg.label}</span>
          </div>
        </div>

        <div className="space-y-2 mb-4">
          <p className="text-sm text-slate-600 font-medium">策略來源：</p>
          <div className="flex flex-wrap gap-2">
            {strategy.sources?.opportunities?.map((item, i) => (
              <Badge key={i} className="bg-blue-100 text-blue-700 border-blue-200">機會: {item}</Badge>
            ))}
            {strategy.sources?.threats?.map((item, i) => (
              <Badge key={i} className="bg-amber-100 text-amber-700 border-amber-200">威脅: {item}</Badge>
            ))}
            {strategy.sources?.strengths?.map((item, i) => (
              <Badge key={i} className="bg-green-100 text-green-700 border-green-200">優勢: {item}</Badge>
            ))}
            {strategy.sources?.weaknesses?.map((item, i) => (
              <Badge key={i} className="bg-red-100 text-red-700 border-red-200">劣勢: {item}</Badge>
            ))}
          </div>
        </div>

        <div className="border-t border-slate-100 pt-3 flex flex-wrap gap-2 items-center">
          <Button
            data-testid={`strategy-include-${index}`}
            onClick={() => handleInclusion('included')}
            disabled={saving || inclusion === 'included'}
            size="sm"
            className={`${inclusion === 'included' ? 'bg-[#0F52BA] hover:bg-[#0F52BA]' : 'bg-[#0F52BA] hover:bg-[#0c4296]'} text-white`}
          >
            {inclusion === 'included' ? <Check className="h-4 w-4 mr-1" /> : null}
            納入待辦事項
          </Button>
          <Button
            data-testid={`strategy-defer-${index}`}
            onClick={() => handleInclusion('deferred')}
            disabled={saving || inclusion === 'deferred'}
            size="sm"
            variant="outline"
            className={`${inclusion === 'deferred' ? 'border-amber-500 text-amber-700 bg-amber-50' : 'border-slate-300 text-slate-700'}`}
          >
            {inclusion === 'deferred' ? <Check className="h-4 w-4 mr-1" /> : null}
            暫緩實施
          </Button>
          {inclusion === 'included' && (
            <Button
              data-testid={`strategy-edit-task-${index}`}
              onClick={() => navigate('/todos')}
              size="sm"
              variant="outline"
              className="border-[#0F52BA] text-[#0F52BA] hover:bg-blue-50 ml-auto"
            >
              編輯待辦詳情 →
            </Button>
          )}
          {strategy.inclusion_updated_at && (
            <span className="text-xs text-slate-400 ml-auto">
              {new Date(strategy.inclusion_updated_at).toLocaleString('zh-TW')}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Step 4: Strategy Analysis
const Step4Strategy = ({ analysisId, analysisData, setAnalysisData, onReset }) => {
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(false);
  
  useEffect(() => {
    if (analysisData?.strategies?.length > 0) {
      setGenerated(true);
    }
  }, [analysisData]);
  
  const handleGenerate = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/analysis/${analysisId}/generate-strategies`);
      const response = await axios.get(`${API}/analysis/${analysisId}`);
      setAnalysisData(response.data);
      setGenerated(true);
      toast.success('策略建議已生成');
    } catch (error) {
      toast.error('生成失敗，請重試');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="max-w-6xl mx-auto animate-in fade-in slide-in-from-right-4 duration-300">
      {!generated ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">策略分析與建議</CardTitle>
            <CardDescription>根據 SWOT 分析結果生成策略建議</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center py-12">
              <p className="text-slate-600 mb-6">點擊下方按鈕，AI 將為您生成策略建議</p>
              <Button
                data-testid="generate-strategies-button"
                onClick={handleGenerate}
                disabled={loading}
                className="bg-[#0F52BA] hover:bg-[#0c4296]"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    生成中...
                  </>
                ) : (
                  '生成策略建議'
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-8">
          {/* SWOT Matrix */}
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">SWOT 矩陣</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-6">
                {/* Strengths */}
                <div className="p-6 border-2 border-[#16A34A] rounded-xl bg-green-50">
                  <h3 className="text-lg font-semibold text-[#16A34A] mb-4">優勢 Strengths</h3>
                  <ul className="space-y-2">
                    {analysisData.selected_strengths.map((item, index) => (
                      <li key={index} className="text-slate-700 flex items-start gap-2">
                        <span className="text-[#16A34A] mt-1">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* Weaknesses */}
                <div className="p-6 border-2 border-[#DC2626] rounded-xl bg-red-50">
                  <h3 className="text-lg font-semibold text-[#DC2626] mb-4">劣勢 Weaknesses</h3>
                  <ul className="space-y-2">
                    {analysisData.selected_weaknesses.map((item, index) => (
                      <li key={index} className="text-slate-700 flex items-start gap-2">
                        <span className="text-[#DC2626] mt-1">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* Opportunities */}
                <div className="p-6 border-2 border-[#2563EB] rounded-xl bg-blue-50">
                  <h3 className="text-lg font-semibold text-[#2563EB] mb-4">機會 Opportunities</h3>
                  <ul className="space-y-2">
                    {analysisData.opportunities
                      .filter(item => (analysisData.selected_opportunities || []).includes(item.issue))
                      .map((item, index) => (
                        <li key={index} className="text-slate-700 flex items-start gap-2">
                          <span className="text-[#2563EB] mt-1">•</span>
                          <span>{item.issue}</span>
                        </li>
                      ))}
                    {(analysisData.custom_opportunities || []).map((item, index) => (
                      <li key={`custom-${index}`} className="text-slate-700 flex items-start gap-2">
                        <span className="text-[#2563EB] mt-1">•</span>
                        <span>{item} <Badge className="ml-1 bg-blue-100 text-blue-700 text-xs">自訂</Badge></span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* Threats */}
                <div className="p-6 border-2 border-[#D97706] rounded-xl bg-amber-50">
                  <h3 className="text-lg font-semibold text-[#D97706] mb-4">威脅 Threats</h3>
                  <ul className="space-y-2">
                    {analysisData.threats
                      .filter(item => (analysisData.selected_threats || []).includes(item.issue))
                      .map((item, index) => (
                        <li key={index} className="text-slate-700 flex items-start gap-2">
                          <span className="text-[#D97706] mt-1">•</span>
                          <span>{item.issue}</span>
                        </li>
                      ))}
                    {(analysisData.custom_threats || []).map((item, index) => (
                      <li key={`custom-${index}`} className="text-slate-700 flex items-start gap-2">
                        <span className="text-[#D97706] mt-1">•</span>
                        <span>{item} <Badge className="ml-1 bg-amber-100 text-amber-700 text-xs">自訂</Badge></span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Strategy Recommendations */}
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">策略建議與納入決定</CardTitle>
              <CardDescription>決定每條策略要納入待辦事項清單或暫緩實施</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Decision Summary */}
              {(() => {
                const total = analysisData.strategies.length;
                const counts = analysisData.strategies.reduce((acc, s) => {
                  const k = s.inclusion || 'pending';
                  acc[k] = (acc[k] || 0) + 1;
                  return acc;
                }, {});
                const included = counts.included || 0;
                const deferred = counts.deferred || 0;
                const pending = counts.pending || 0;
                const pct = total > 0 ? Math.round((included / total) * 100) : 0;
                return (
                  <div data-testid="strategy-decision-summary" className="mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-700">納入決定進度</span>
                      <span className="text-sm font-bold text-[#0F52BA]">{included + deferred}/{total} 已決定</span>
                    </div>
                    <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-blue-500 to-amber-500 transition-all duration-500"
                        style={{ width: `${total > 0 ? Math.round(((included + deferred) / total) * 100) : 0}%` }}
                      />
                    </div>
                    <div className="flex gap-4 mt-3 text-xs">
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500"></span>納入待辦 {included}</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500"></span>暫緩實施 {deferred}</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-400"></span>未決定 {pending}</span>
                      {included > 0 && (
                        <span className="ml-auto text-[#0F52BA] font-medium">納入比例 {pct}%</span>
                      )}
                    </div>
                  </div>
                );
              })()}
              
              <div className="space-y-4">
                {analysisData.strategies.map((strategy, index) => (
                  <StrategyCard
                    key={index}
                    strategy={strategy}
                    index={index}
                    analysisId={analysisId}
                    onUpdate={(idx, patch) => {
                      const next = { ...analysisData };
                      next.strategies = [...analysisData.strategies];
                      next.strategies[idx] = { ...next.strategies[idx], ...patch };
                      setAnalysisData(next);
                    }}
                  />
                ))}
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <div className="flex-1">
                  <ExportButtons analysisId={analysisId} />
                </div>
                <Button
                  data-testid="complete-button"
                  onClick={onReset}
                  className="bg-[#0F52BA] hover:bg-[#0c4296] h-11 px-8"
                >
                  完成分析
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// History View
const HistoryView = () => {
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdminView, setIsAdminView] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  
  useEffect(() => {
    fetchAnalyses();
  }, []);
  
  const fetchAnalyses = async () => {
    try {
      const response = await axios.get(`${API}/analysis/list/all`);
      setAnalyses(response.data.data);
      setIsAdminView(!!response.data.is_admin);
    } catch (error) {
      toast.error('載入歷史紀錄失敗');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDelete = async (id, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/analysis/${id}`);
      toast.success('已刪除分析');
      fetchAnalyses();
    } catch (error) {
      toast.error('刪除失敗');
    }
  };
  
  const getStatusText = (status) => {
    const statusMap = {
      'step1': '基本資料',
      'step2': '外部環境分析',
      'step3': '內部條件分析',
      'completed': '已完成'
    };
    return statusMap[status] || status;
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#0F52BA]" />
      </div>
    );
  }
  
  return (
    <div>
      <h2 className="text-3xl font-bold text-slate-900 mb-6">
        {isAdminView ? '所有使用者的分析紀錄' : '我的歷史紀錄'}
        {isAdminView && <Badge className="ml-3 bg-amber-100 text-amber-700 border-amber-300"><Shield size={12} className="mr-1" />Admin 視角</Badge>}
      </h2>
      
      {analyses.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <p className="text-slate-500">尚無分析記錄</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {analyses.map((analysis) => (
            <Card key={analysis.id} data-testid={`history-item-${analysis.id}`} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <h3 className="text-lg font-semibold text-slate-800">
                        {analysis.industry_type || '未命名分析'}
                      </h3>
                      <Badge className="bg-blue-100 text-blue-700">{analysis.analysis_type || '經營管理'}</Badge>
                      <Badge variant="outline">{getStatusText(analysis.status)}</Badge>
                      {isAdminView && analysis.owner && (
                        <Badge data-testid={`owner-badge-${analysis.id}`} className="bg-purple-100 text-purple-700 border-purple-300">
                          <UserIcon size={11} className="mr-1" />
                          {analysis.owner.email}
                        </Badge>
                      )}
                    </div>
                    <p className="text-slate-600 text-sm mb-1">
                      <span className="font-medium">企業規模：</span>{analysis.company_size || '-'}
                    </p>
                    <p className="text-slate-600 text-sm mb-1">
                      <span className="font-medium">核心業務：</span>
                      {analysis.core_business ? `${analysis.core_business.substring(0, 60)}${analysis.core_business.length > 60 ? '...' : ''}` : '-'}
                    </p>
                    <p className="text-slate-600 text-sm mb-2">
                      <span className="font-medium">目標市場：</span>{analysis.target_market_region || '-'}
                      {analysis.target_customers && <span className="ml-2 text-slate-500">/ {analysis.target_customers}</span>}
                    </p>
                    <p className="text-slate-400 text-xs">
                      建立於: {new Date(analysis.created_at).toLocaleString('zh-TW')}
                    </p>
                  </div>
                  
                  <div className="flex gap-2 items-start">
                    {analysis.status === 'completed' && (
                      <Button
                        data-testid={`quick-export-pdf-${analysis.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(`${API}/analysis/${analysis.id}/export/pdf`, '_blank');
                        }}
                        variant="outline"
                        size="sm"
                        className="border-[#0F52BA] text-[#0F52BA] hover:bg-blue-50"
                        title="匯出 PDF"
                      >
                        <FileDown size={16} />
                      </Button>
                    )}
                    {analysis.status === 'completed' ? (
                      <Button
                        data-testid={`view-analysis-${analysis.id}`}
                        onClick={() => navigate(`/analysis/${analysis.id}`)}
                        variant="outline"
                        size="sm"
                      >
                        查看
                      </Button>
                    ) : (
                      <Button
                        data-testid={`continue-analysis-${analysis.id}`}
                        onClick={() => navigate(`/continue/${analysis.id}`)}
                        size="sm"
                        className="bg-[#0F52BA] hover:bg-[#0c4296] text-white"
                      >
                        繼續編輯
                      </Button>
                    )}
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          data-testid={`delete-analysis-${analysis.id}`}
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>確認刪除</AlertDialogTitle>
                          <AlertDialogDescription>
                            確定要刪除這筆分析記錄嗎？此操作無法復原。
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>取消</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={(e) => handleDelete(analysis.id, e)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            刪除
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// Analysis Wizard
const AnalysisWizard = ({ resumeAnalysisId }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [analysisId, setAnalysisId] = useState(resumeAnalysisId || null);
  const [analysisData, setAnalysisData] = useState(null);
  const [loadingResume, setLoadingResume] = useState(!!resumeAnalysisId);
  const navigate = useNavigate();
  
  const steps = ['step1', 'step2', 'step3', 'completed'];
  const statusToStep = { step1: 0, step2: 1, step3: 2, completed: 3 };
  
  // 載入要繼續編輯的分析資料
  useEffect(() => {
    if (resumeAnalysisId) {
      (async () => {
        try {
          const res = await axios.get(`${API}/analysis/${resumeAnalysisId}`);
          setAnalysisData(res.data);
          setAnalysisId(res.data.id);
          // 跳到對應步驟
          const targetStep = statusToStep[res.data.status] ?? 0;
          setCurrentStep(targetStep);
          toast.success(`已載入分析，從第 ${targetStep + 1} 步繼續`);
        } catch (e) {
          toast.error('載入分析失敗');
          navigate('/history');
        } finally {
          setLoadingResume(false);
        }
      })();
    }
  }, [resumeAnalysisId, navigate]);
  
  const handleNext = () => {
    setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
  };
  
  const handleStepClick = (stepIndex) => {
    // 允許往回跳到已完成的步驟
    if (stepIndex < currentStep && analysisId) {
      setCurrentStep(stepIndex);
    }
  };
  
  const handleReset = () => {
    setCurrentStep(0);
    setAnalysisId(null);
    setAnalysisData(null);
    toast.success('分析已完成並儲存');
    if (resumeAnalysisId) {
      navigate('/history');
    }
  };
  
  if (loadingResume) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#0F52BA]" />
      </div>
    );
  }
  
  return (
    <div>
      <h2 className="text-3xl font-bold text-slate-900 mb-8">
        {resumeAnalysisId ? '繼續編輯 SWOT 分析' : '新增 SWOT 分析'}
      </h2>
      
      <Stepper currentStep={steps[currentStep]} onStepClick={handleStepClick} canNavigate={!!analysisId} />
      
      {currentStep === 0 && (
        <Step1BasicInfo
          analysisId={analysisId}
          setAnalysisId={setAnalysisId}
          onNext={handleNext}
          existingData={analysisData}
          setAnalysisData={setAnalysisData}
        />
      )}
      
      {currentStep === 1 && (
        <Step2PESTLE
          analysisId={analysisId}
          analysisData={analysisData}
          setAnalysisData={setAnalysisData}
          onNext={handleNext}
        />
      )}
      
      {currentStep === 2 && (
        <Step3SW
          analysisId={analysisId}
          analysisData={analysisData}
          setAnalysisData={setAnalysisData}
          onNext={handleNext}
        />
      )}
      
      {currentStep === 3 && (
        <Step4Strategy
          analysisId={analysisId}
          analysisData={analysisData}
          setAnalysisData={setAnalysisData}
          onReset={handleReset}
        />
      )}
    </div>
  );
};

// ---------------- Todo Item Card ----------------
const TodoItemCard = ({ todo, isAdminView, onChange }) => {
  const [expanded, setExpanded] = useState(false);
  const [assignee, setAssignee] = useState(todo.task.assignee || '');
  const [execSummary, setExecSummary] = useState(todo.task.execution_summary || '');
  const [tStart, setTStart] = useState(todo.task.timeline_start || '');
  const [tEnd, setTEnd] = useState(todo.task.timeline_end || '');
  const [deliverables, setDeliverables] = useState(todo.task.deliverables || '');
  const [progress, setProgress] = useState(todo.task.progress || 0);
  const [progressNote, setProgressNote] = useState('');
  const [saving, setSaving] = useState(false);
  
  const url = `${API}/analysis/${todo.analysis_id}/strategies/${todo.strategy_index}`;
  
  const saveDetails = async () => {
    setSaving(true);
    try {
      await axios.put(`${url}/task`, {
        assignee,
        execution_summary: execSummary,
        timeline_start: tStart,
        timeline_end: tEnd,
        deliverables,
        progress
      });
      toast.success('待辦詳情已儲存');
      if (onChange) onChange();
    } catch (e) {
      toast.error('儲存失敗');
    } finally {
      setSaving(false);
    }
  };
  
  const addProgressFeedback = async () => {
    setSaving(true);
    try {
      await axios.post(`${url}/progress`, { progress, note: progressNote });
      setProgressNote('');
      toast.success(`進度已更新為 ${progress}%`);
      if (onChange) onChange();
    } catch (e) {
      toast.error('更新失敗');
    } finally {
      setSaving(false);
    }
  };
  
  const removeFromTodos = async () => {
    setSaving(true);
    try {
      await axios.put(`${url}/inclusion`, { inclusion: 'deferred' });
      toast.success('已移出待辦事項');
      if (onChange) onChange();
    } catch (e) {
      toast.error('操作失敗');
    } finally {
      setSaving(false);
    }
  };
  
  const progressColor = progress >= 100 ? 'bg-green-500' : progress >= 50 ? 'bg-blue-500' : progress > 0 ? 'bg-amber-500' : 'bg-slate-300';
  
  return (
    <Card data-testid={`todo-item-${todo.analysis_id}-${todo.strategy_index}`} className="border-l-4 border-l-[#0F52BA]">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <Badge variant="outline" className="text-xs">{todo.analysis_type}</Badge>
              <Badge className="bg-slate-100 text-slate-600 text-xs">{todo.analysis_title}</Badge>
              {isAdminView && todo.owner && (
                <Badge className="bg-purple-100 text-purple-700 border-purple-300 text-xs">
                  <UserIcon size={10} className="mr-1" />{todo.owner.email}
                </Badge>
              )}
            </div>
            <p className="text-slate-800 font-medium">{todo.strategy}</p>
          </div>
          <div className="text-right shrink-0">
            <div className="text-2xl font-bold text-[#0F52BA]">{progress}%</div>
            <div className="text-xs text-slate-500">完成度</div>
          </div>
        </div>
        
        <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden mb-3">
          <div className={`h-full ${progressColor} transition-all duration-500`} style={{ width: `${progress}%` }}></div>
        </div>
        
        {/* 摘要顯示 */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-3">
          <div>
            <p className="text-xs text-slate-500 mb-0.5">負責人</p>
            <p className="font-medium text-slate-700">{assignee || '-'}</p>
          </div>
          <div className="col-span-2 md:col-span-2">
            <p className="text-xs text-slate-500 mb-0.5">時程</p>
            <p className="font-medium text-slate-700">{tStart || '-'} ~ {tEnd || '-'}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500 mb-0.5">交付標的</p>
            <p className="font-medium text-slate-700 truncate" title={deliverables}>{deliverables || '-'}</p>
          </div>
        </div>
        
        <div className="flex gap-2 items-center">
          <Button
            data-testid={`todo-expand-${todo.analysis_id}-${todo.strategy_index}`}
            size="sm"
            variant="outline"
            onClick={() => setExpanded(e => !e)}
          >
            <ChevronRight className={`h-4 w-4 mr-1 transition-transform ${expanded ? 'rotate-90' : ''}`} />
            {expanded ? '收合' : '編輯詳情'}
          </Button>
          <Button
            data-testid={`todo-remove-${todo.analysis_id}-${todo.strategy_index}`}
            size="sm"
            variant="outline"
            onClick={removeFromTodos}
            disabled={saving}
            className="text-amber-600 hover:bg-amber-50"
          >
            移出待辦 (改暫緩)
          </Button>
        </div>
        
        {expanded && (
          <div className="mt-4 pt-4 border-t border-slate-100 space-y-4 animate-in fade-in duration-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">負責人</label>
                <Input
                  data-testid={`todo-assignee-${todo.analysis_id}-${todo.strategy_index}`}
                  value={assignee} onChange={e => setAssignee(e.target.value)}
                  placeholder="例如：王經理 / 業務團隊"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">時程規劃 (起 ~ 訖)</label>
                <div className="flex gap-2">
                  <Input
                    type="date" data-testid={`todo-timeline-start-${todo.analysis_id}-${todo.strategy_index}`}
                    value={tStart} onChange={e => setTStart(e.target.value)}
                  />
                  <Input
                    type="date" data-testid={`todo-timeline-end-${todo.analysis_id}-${todo.strategy_index}`}
                    value={tEnd} onChange={e => setTEnd(e.target.value)}
                  />
                </div>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">執行重點摘要</label>
              <Textarea
                data-testid={`todo-summary-${todo.analysis_id}-${todo.strategy_index}`}
                value={execSummary} onChange={e => setExecSummary(e.target.value)}
                placeholder="關鍵行動、里程碑、資源需求..."
                className="min-h-[80px]"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">交付標的</label>
              <Textarea
                data-testid={`todo-deliverables-${todo.analysis_id}-${todo.strategy_index}`}
                value={deliverables} onChange={e => setDeliverables(e.target.value)}
                placeholder="可衡量的產出，例如：3 家面板廠 POC 報告"
                className="min-h-[60px]"
              />
            </div>
            
            <Button
              data-testid={`todo-save-${todo.analysis_id}-${todo.strategy_index}`}
              onClick={saveDetails}
              disabled={saving}
              className="bg-[#0F52BA] hover:bg-[#0c4296]"
              size="sm"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              儲存待辦詳情
            </Button>
            
            {/* 進度回饋區 */}
            <div className="border-t border-slate-100 pt-4">
              <p className="text-sm font-medium text-slate-700 mb-2">進度回饋</p>
              <div className="flex items-center gap-3 mb-2">
                <input
                  type="range" min="0" max="100" step="5"
                  data-testid={`todo-progress-slider-${todo.analysis_id}-${todo.strategy_index}`}
                  value={progress} onChange={e => setProgress(parseInt(e.target.value))}
                  className="flex-1 accent-[#0F52BA]"
                />
                <span className="font-bold text-[#0F52BA] w-12 text-right">{progress}%</span>
              </div>
              <Textarea
                data-testid={`todo-progress-note-${todo.analysis_id}-${todo.strategy_index}`}
                value={progressNote} onChange={e => setProgressNote(e.target.value)}
                placeholder="本次進度更新說明 (選填)..."
                className="min-h-[50px] mb-2"
              />
              <Button
                data-testid={`todo-feedback-submit-${todo.analysis_id}-${todo.strategy_index}`}
                onClick={addProgressFeedback}
                disabled={saving}
                size="sm"
                variant="outline"
                className="border-[#0F52BA] text-[#0F52BA] hover:bg-blue-50"
              >
                送出進度回饋
              </Button>
              
              {/* 歷史進度紀錄 */}
              {todo.task.progress_updates && todo.task.progress_updates.length > 0 && (
                <div className="mt-3 space-y-1.5">
                  <p className="text-xs font-medium text-slate-500">歷史回饋</p>
                  <div className="max-h-40 overflow-y-auto space-y-1.5">
                    {todo.task.progress_updates.slice().reverse().map((u, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs p-2 bg-slate-50 rounded">
                        <Badge className="bg-blue-100 text-blue-700 shrink-0">{u.progress}%</Badge>
                        <div className="flex-1 min-w-0">
                          {u.note && <p className="text-slate-700">{u.note}</p>}
                          <p className="text-slate-400 mt-0.5">
                            {u.user_name || '使用者'} · {new Date(u.created_at).toLocaleString('zh-TW')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ---------------- Todo List Page ----------------
const TodoListView = () => {
  const [todos, setTodos] = useState([]);
  const [stats, setStats] = useState({ total: 0, completed: 0, overall_progress: 0 });
  const [isAdminView, setIsAdminView] = useState(false);
  const [loading, setLoading] = useState(true);
  
  const fetchTodos = async () => {
    try {
      const res = await axios.get(`${API}/todos/all`);
      setTodos(res.data.data);
      setStats(res.data.stats);
      setIsAdminView(!!res.data.is_admin);
    } catch (e) {
      toast.error('載入待辦事項失敗');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => { fetchTodos(); }, []);
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#0F52BA]" />
      </div>
    );
  }
  
  return (
    <div>
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-3xl font-bold text-slate-900">
          {isAdminView ? '所有使用者的待辦事項' : '我的待辦事項清單'}
          {isAdminView && <Badge className="ml-3 bg-amber-100 text-amber-700 border-amber-300"><Shield size={12} className="mr-1" />Admin 視角</Badge>}
        </h2>
      </div>
      
      {/* 整體完成度卡片 */}
      <Card data-testid="todos-overall-stats" className="mb-6 bg-gradient-to-r from-blue-50 to-slate-50 border-[#0F52BA]/20">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-sm text-slate-600 font-medium">整體完成度</p>
              <p className="text-4xl font-bold text-[#0F52BA] mt-1">{stats.overall_progress}%</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-600">
                <span className="font-bold text-slate-800 text-lg">{stats.completed}</span> / {stats.total} 已完成
              </p>
              <p className="text-xs text-slate-500 mt-1">執行中 {stats.total - stats.completed} 項</p>
            </div>
          </div>
          <div className="w-full h-3 bg-white rounded-full overflow-hidden border border-slate-200">
            <div
              className="h-full bg-gradient-to-r from-blue-500 to-green-500 transition-all duration-700"
              style={{ width: `${stats.overall_progress}%` }}
            />
          </div>
        </CardContent>
      </Card>
      
      {todos.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <p className="text-slate-500 mb-2">尚無待辦事項</p>
            <p className="text-sm text-slate-400">前往策略分析頁面，將策略「納入待辦事項」即可在此追蹤執行進度</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {todos.map((todo, idx) => (
            <TodoItemCard
              key={`${todo.analysis_id}-${todo.strategy_index}`}
              todo={todo}
              isAdminView={isAdminView}
              onChange={fetchTodos}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// View Existing Analysis
const ViewAnalysis = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [analysisData, setAnalysisData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchAnalysis();
  }, [id]);
  
  const fetchAnalysis = async () => {
    try {
      const response = await axios.get(`${API}/analysis/${id}`);
      setAnalysisData(response.data);
    } catch (error) {
      toast.error('載入分析失敗');
      navigate('/history');
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#0F52BA]" />
      </div>
    );
  }
  
  if (!analysisData) return null;
  
  return (
    <div className="max-w-6xl mx-auto">
      <Button
        onClick={() => navigate('/history')}
        variant="outline"
        className="mb-6"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        返回歷史紀錄
      </Button>
      
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-3xl font-bold text-slate-900">SWOT 分析詳情</h2>
        <div className="flex gap-2 items-center">
          <Button
            data-testid="edit-analysis-button"
            onClick={() => navigate(`/continue/${id}`)}
            variant="outline"
            className="border-[#0F52BA] text-[#0F52BA] hover:bg-blue-50"
          >
            重新編輯
          </Button>
          {analysisData.status === 'completed' && <ExportButtons analysisId={id} />}
        </div>
      </div>
      
      {/* Basic Info */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>基本資料</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-slate-600 mb-1">產業類型</p>
              <p className="text-slate-800">{analysisData.industry_type || '-'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-slate-600 mb-1">企業規模</p>
              <p className="text-slate-800">{analysisData.company_size || '-'}</p>
            </div>
            <div className="col-span-2">
              <p className="text-sm font-medium text-slate-600 mb-1">核心業務</p>
              <p className="text-slate-800">{analysisData.core_business || '-'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-slate-600 mb-1">目標市場地區</p>
              <p className="text-slate-800">{analysisData.target_market_region || '-'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-slate-600 mb-1">目標客群</p>
              <p className="text-slate-800">{analysisData.target_customers || '-'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-slate-600 mb-1">分析項目</p>
              <Badge className="bg-blue-100 text-blue-700">{analysisData.analysis_type || '經營管理'}</Badge>
            </div>
            <div className="col-span-2">
              <p className="text-sm font-medium text-slate-600 mb-1">市場特性</p>
              <p className="text-slate-800">{analysisData.market_characteristics || '-'}</p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Show completed analysis if available */}
      {analysisData.status === 'completed' && (
        <Step4Strategy
          analysisId={id}
          analysisData={analysisData}
          setAnalysisData={setAnalysisData}
          onReset={() => navigate('/history')}
        />
      )}
    </div>
  );
};

// Resume Wizard wrapper - read id from URL
const ResumeWizard = () => {
  const { id } = useParams();
  return <AnalysisWizard resumeAnalysisId={id} />;
};

// Main App Component
function App() {
  const [currentView, setCurrentView] = useState('new');
  
  return (
    <BrowserRouter>
      <Toaster position="top-center" />
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <RootRouter currentView={currentView} setCurrentView={setCurrentView} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/history"
            element={
              <ProtectedRoute>
                <Layout currentView="history" setCurrentView={setCurrentView}>
                  <HistoryView />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/todos"
            element={
              <ProtectedRoute>
                <Layout currentView={currentView} setCurrentView={setCurrentView}>
                  <TodoListView />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/analysis/:id"
            element={
              <ProtectedRoute>
                <Layout currentView="history" setCurrentView={setCurrentView}>
                  <ViewAnalysis />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/continue/:id"
            element={
              <ProtectedRoute>
                <Layout currentView="history" setCurrentView={setCurrentView}>
                  <ResumeWizard />
                </Layout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

// 處理首頁的 callback hash 情況 - 若 URL 含 session_id, 走 AuthCallback 流程
const RootRouter = ({ currentView, setCurrentView }) => {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Layout currentView={currentView} setCurrentView={setCurrentView}>
      {currentView === 'new' ? <AnalysisWizard /> : <HistoryView />}
    </Layout>
  );
};

export default App;